import { Button, Col, Form, Row, Stack } from "react-bootstrap"
import { ClientData, SESSION_TIMEOUT_CONSTANTS, DEVICE_LIMIT_CONSTANTS, LOGIN_RETRY_CONSTANTS } from "../../api/apps/data"
import { useState } from "react"
import { useApps } from "../../api/apps/store"
import TimeoutInput from "../../components/TimeoutInput"
import InputWithHelp from "../../components/InputWithHelp"
import HelpTooltip from "../../components/HelpTooltip"
import CDropdown from "../../components/CDropdown"
import { SessionTimeoutValidation, TIMEOUT_HELP_TEXT } from "../../utils/timeHelpers"

const platforms = ["Web", "IOS", "Android", "Desktop"]

interface EditClientViewProps {
    client: ClientData
    onUpdated: () => void
    onCancel: () => void
}

const EditClientView: React.FC<EditClientViewProps> = ({ client, onUpdated, onCancel }) => {
    const [desc, setDesc] = useState(client.desc || "")
    const [redirect, setRedirect] = useState(client.redirect || "")
    const [updating, setUpdating] = useState(false)
    const [platform, setPlatform] = useState(client.platform || "Web")
    const [domain, setDomain] = useState(client.domain || "")
    const [customHash, setCustomHash] = useState(client.custom_hash || false)
    const [sessionActivityTimeout, setSessionActivityTimeout] = useState<number>(
        client.session_activity_timeout || SESSION_TIMEOUT_CONSTANTS.DEFAULT_ACTIVITY_TIMEOUT
    )
    const [sessionTTL, setSessionTTL] = useState<number>(
        client.session_ttl || SESSION_TIMEOUT_CONSTANTS.DEFAULT_TTL
    )
    const [allowDelete, setAllowDelete] = useState(client.allow_delete || false)
    const [allowMultipleSessions, setAllowMultipleSessions] = useState(client.allow_multiple_sessions !== false)
    const [deviceLimit, setDeviceLimit] = useState<number>(
        client.device_limit || DEVICE_LIMIT_CONSTANTS.DEFAULT
    )
    const [loginRetryLimit, setLoginRetryLimit] = useState<number>(
        client.login_retry_limit || LOGIN_RETRY_CONSTANTS.DEFAULT
    )
    const [newSessionOnReauth, setNewSessionOnReauth] = useState(client.new_session_on_reauth || false)
    const [err, setErr] = useState({ name: "", redirect: "", domain: "", activityTimeout: "", ttl: "", deviceLimit: "", loginRetryLimit: "" })
    const { updateClient } = useApps(state => state)

    const _onUpdate = () => {
        setUpdating(true)
        let chash = customHash
        if (platform === "Web") {
            setCustomHash(false)
            chash = false
        }

        // Validate timeout values
        const activityValidation = SessionTimeoutValidation.validateActivityTimeout(sessionActivityTimeout)
        const ttlValidation = SessionTimeoutValidation.validateTTL(sessionTTL)

        setErr({ name: "", redirect: "", domain: "", activityTimeout: "", ttl: "", deviceLimit: "", loginRetryLimit: "" })

        if (!activityValidation.isValid) {
            setErr(prev => ({ ...prev, activityTimeout: activityValidation.error! }))
            setUpdating(false)
            return
        }

        if (!ttlValidation.isValid) {
            setErr(prev => ({ ...prev, ttl: ttlValidation.error! }))
            setUpdating(false)
            return
        }

        // Validate device limit
        if (deviceLimit < DEVICE_LIMIT_CONSTANTS.MIN || deviceLimit > DEVICE_LIMIT_CONSTANTS.MAX) {
            setErr(prev => ({ ...prev, deviceLimit: `Device limit must be between ${DEVICE_LIMIT_CONSTANTS.MIN} and ${DEVICE_LIMIT_CONSTANTS.MAX}` }))
            setUpdating(false)
            return
        }

        // Validate login retry limit
        if (loginRetryLimit < LOGIN_RETRY_CONSTANTS.MIN || loginRetryLimit > LOGIN_RETRY_CONSTANTS.MAX) {
            setErr(prev => ({ ...prev, loginRetryLimit: `Login retry limit must be between ${LOGIN_RETRY_CONSTANTS.MIN} and ${LOGIN_RETRY_CONSTANTS.MAX}` }))
            setUpdating(false)
            return
        }

        updateClient({
            id: client.id,
            desc: desc,
            secret: client.secret,
            redirect: redirect,
            platform: platform,
            domain: domain,
            custom_hash: chash,
            session_activity_timeout: sessionActivityTimeout,
            session_ttl: sessionTTL,
            allow_delete: allowDelete,
            allow_multiple_sessions: allowMultipleSessions,
            device_limit: deviceLimit,
            login_retry_limit: loginRetryLimit,
            new_session_on_reauth: newSessionOnReauth
        })
            .then(res => {
                switch (res.Code || res) {
                    case "200":
                        onUpdated()
                        break
                    case "incomplete_param":
                        setErr({ name: "Required", redirect: "", domain: "", activityTimeout: "", ttl: "", deviceLimit: "", loginRetryLimit: "" })
                        break;

                    default:
                        break;
                }
            })
            .catch(err => {
                console.error("Update failed:", err)
            })
            .finally(() => setUpdating(false))
    }

    return (
        <Stack gap={2} style={{ padding: "10px" }}>
            <Row>
                <Col>
                    <InputWithHelp
                        label="Name"
                        helpText="A descriptive name for this OAuth client application. This helps identify the client in the admin interface."
                        className="form-control modal-input"
                        value={desc}
                        errorMessage={err.name}
                        error={!!err.name}
                        disabled={updating}
                        onChange={(e: any) => setDesc(e.target.value)}
                        placeholder="Enter client name"
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <InputWithHelp
                        label="Redirect URL"
                        helpText="The URL where users will be redirected after authentication. Must be a valid HTTPS URL for security."
                        className="form-control modal-input"
                        value={redirect}
                        errorMessage={err.redirect}
                        error={!!err.redirect}
                        disabled={updating}
                        onChange={(e: any) => setRedirect(e.target.value)}
                        placeholder="https://your-app.com/callback"
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <InputWithHelp
                        label="Domain"
                        helpText="Unique domain identifier for this client. Users will be exclusive to this domain, preventing conflicts between different applications. Usually this is company id."
                        className="form-control modal-input"
                        value={domain}
                        errorMessage={err.domain}
                        error={!!err.domain}
                        disabled={updating}
                        onChange={(e: any) => setDomain(e.target.value.replace(/[^a-zA-Z0-9._]/g, ''))}
                        placeholder="my-app-domain"
                    />
                </Col>
            </Row>
            <Row>
                <Col style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>Platform</label>
                    <HelpTooltip content="The target platform for this client application. Different platforms have different security requirements." />
                </Col>
                <Col>
                    <CDropdown
                        selected={platforms.findIndex(item => item === platform)}
                        items={platforms}
                        onSelected={(key, index) => !updating && setPlatform(key!)}
                    />
                </Col>
            </Row>
            {platform !== "Web" && <Row>
                <Col style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>Custom Hashing</label>
                    <HelpTooltip content="Enable custom password hashing on the client side. Only available for non-Web platforms for enhanced security." />
                </Col>
                <Col>
                    <Form.Check
                        type="switch"
                        checked={customHash}
                        disabled={updating}
                        onChange={(e: any) => setCustomHash(e.target.checked)}
                    />
                </Col>
            </Row>}
            <Row>
                <Col>
                    <TimeoutInput
                        label="Session Activity Timeout"
                        helpText={TIMEOUT_HELP_TEXT.activityTimeout}
                        value={sessionActivityTimeout}
                        onChange={setSessionActivityTimeout}
                        error={err.activityTimeout}
                        disabled={updating}
                        placeholder="Enter activity timeout"
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TimeoutInput
                        label="Session TTL"
                        helpText={TIMEOUT_HELP_TEXT.ttl}
                        value={sessionTTL}
                        onChange={setSessionTTL}
                        error={err.ttl}
                        disabled={updating}
                        placeholder="Enter session TTL"
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <InputWithHelp
                        label="Device Limit"
                        helpText={`Maximum number of devices a user can be logged in from simultaneously. Must be between ${DEVICE_LIMIT_CONSTANTS.MIN} and ${DEVICE_LIMIT_CONSTANTS.MAX}.`}
                        type="number"
                        className="form-control modal-input"
                        value={deviceLimit}
                        errorMessage={err.deviceLimit}
                        error={!!err.deviceLimit}
                        disabled={updating}
                        onChange={(e: any) => setDeviceLimit(parseInt(e.target.value) || DEVICE_LIMIT_CONSTANTS.DEFAULT)}
                        placeholder={`Default: ${DEVICE_LIMIT_CONSTANTS.DEFAULT}`}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <InputWithHelp
                        label="Login Retry Limit"
                        helpText={`Maximum number of failed login attempts before blocking the account. Must be between ${LOGIN_RETRY_CONSTANTS.MIN} and ${LOGIN_RETRY_CONSTANTS.MAX}.`}
                        type="number"
                        className="form-control modal-input"
                        value={loginRetryLimit}
                        errorMessage={err.loginRetryLimit}
                        error={!!err.loginRetryLimit}
                        disabled={updating}
                        onChange={(e: any) => setLoginRetryLimit(parseInt(e.target.value) || LOGIN_RETRY_CONSTANTS.DEFAULT)}
                        placeholder={`Default: ${LOGIN_RETRY_CONSTANTS.DEFAULT}`}
                    />
                </Col>
            </Row>
            <Row>
                <Col style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>Allow Account Deletion</label>
                    <HelpTooltip content="Allow users from this client to delete their own accounts. This action is irreversible." />
                </Col>
                <Col>
                    <Form.Check
                        type="switch"
                        checked={allowDelete}
                        disabled={updating}
                        onChange={(e: any) => setAllowDelete(e.target.checked)}
                    />
                </Col>
            </Row>
            <Row>
                <Col style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>Allow Multiple Sessions</label>
                    <HelpTooltip content="Control whether users can have multiple active sessions. When disabled, logging in will terminate all other sessions for this app." />
                </Col>
                <Col>
                    <Form.Check
                        type="switch"
                        checked={allowMultipleSessions}
                        disabled={updating}
                        onChange={(e: any) => setAllowMultipleSessions(e.target.checked)}
                    />
                </Col>
            </Row>
            <Row>
                <Col style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>New Session On Reauth</label>
                    <HelpTooltip content="If enabled, reauthentication creates a new session ID and terminates the old one. Use this to prevent session ID reuse abuse or session fixation attacks." />
                </Col>
                <Col>
                    <Form.Check
                        type="switch"
                        checked={newSessionOnReauth}
                        disabled={updating}
                        onChange={(e: any) => setNewSessionOnReauth(e.target.checked)}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                        <Button variant="outline-secondary" disabled={updating} onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button className="btn blur-btn" disabled={updating} onClick={_onUpdate}>
                            {updating ? "Updating..." : "Update"}
                        </Button>
                    </div>
                </Col>
            </Row>
        </Stack>
    )
}

export default EditClientView